/**
 * Capability matrix of the editor modes: which selection kinds (and planar element types) a mode
 * can hold, which sub-selections are meaningful in it, and which page it belongs to. This is the
 * single source of truth the reconciler (`reconcile.ts`) consults on every mode switch, page
 * switch and selection — a selection survives a transition exactly when the target mode's spec
 * still allows it.
 *
 * Plan-element selections (comment/table/image card/label) are deliberately outside this matrix:
 * they live in the store's separate `planSelection` slot, orthogonal to the mode and to the primary
 * selection, so clicking a comment in the scene selects it (in-scene HUD) without leaving the
 * current mode or dropping the selected element; the drawer form only opens in the dedicated plan
 * modes.
 */

interface ModeSelectsSpec {
  kinds: readonly SceneSelectionKind[]
  /** For `'planar'`: which element types are valid; omitted means every planar type. */
  planarTypes?: readonly PlanarSelectedElementType[]
  /** Whether a multi-selection (`isMulti` or more than one guid) may live in this mode. */
  multi: boolean
}

interface ModeSpec {
  /** Page the mode belongs to; `'*'` = usable on any editor page (e.g. `neutral`, `comments`). */
  page: EditorPage | '*'
  selects: ModeSelectsSpec
  subSelects: readonly SceneSubSelectionKind[]
}

const WALL_TYPES = ['inner_walls', 'outer_walls'] as const

const ELEMENT_SUBSELECTS = ['connector', 'vertical-transmitter', 'load', 'stiffening'] as const

const ALL_SUBSELECTS = [
  'opening',
  'connector',
  'vertical-transmitter',
  'load',
  'stiffening',
  'member-position',
] as const

/** Spec of a StructuralPlanning element mode: single selection of the given planar types. */
const elementMode = (
  planarTypes: readonly PlanarSelectedElementType[],
  subSelects: readonly SceneSubSelectionKind[] = ELEMENT_SUBSELECTS,
): ModeSpec => ({
  page: 'structural-planning',
  selects: { kinds: ['planar'], planarTypes, multi: false },
  subSelects,
})

/** Spec of a drawing mode: the element being drawn is held as a single planar selection. */
const drawMode = (
  planarTypes: readonly PlanarSelectedElementType[],
  subSelects: readonly SceneSubSelectionKind[] = [],
): ModeSpec => ({
  page: 'structural-planning',
  selects: { kinds: ['planar'], planarTypes, multi: false },
  subSelects,
})

/** Spec of a mode that holds no scene selection at all (tool panes, pick flows). */
const noSelectionMode = (page: EditorPage | '*' = 'structural-planning'): ModeSpec => ({
  page,
  selects: { kinds: [], multi: false },
  subSelects: [],
})

/**
 * Spec of a plan-element mode (the drawer form of a table/image card, the legacy comments mode):
 * no primary selection — the plan element itself is held in the mode-independent `planSelection`.
 */
const planElementMode = (): ModeSpec => ({
  page: '*',
  selects: { kinds: [], multi: false },
  subSelects: [],
})

const ifcImportMode = (): ModeSpec => ({
  page: 'ifc-import',
  selects: { kinds: ['ifc', 'planar'], multi: true },
  subSelects: ['opening'],
})

const MODE_SPECS: Record<EditorMode, ModeSpec> = {
  /** No tool active — everything is selectable so browsing never loses a selection. */
  neutral: {
    page: '*',
    selects: {
      kinds: ['planar', 'stiffening', 'member-position', 'anchor', 'shear-anchor', 'ifc'],
      multi: true,
    },
    subSelects: ALL_SUBSELECTS,
  },

  // --- StructuralPlanning element modes (mode ⇔ kind of the selected element) ---
  inner_walls: elementMode(WALL_TYPES, ['opening', ...ELEMENT_SUBSELECTS]),
  outer_walls: elementMode(WALL_TYPES, ['opening', ...ELEMENT_SUBSELECTS]),
  slabs: elementMode(['slabs']),
  roof_slabs: elementMode(['roof_slabs']),
  vertical_slabs: elementMode(['vertical_slabs']),
  vertical_roof_slabs: elementMode(['vertical_roof_slabs']),
  beams: elementMode(['beams']),
  columns: elementMode(['columns']),
  purlins: elementMode(['purlins']),
  foundation: elementMode(['foundation']),
  slab_beams: elementMode(['slab_beams']),
  roof_slab_beams: elementMode(['roof_slab_beams']),
  standard_rips: elementMode(['standard_rips']),
  rips: elementMode(['rips', 'rip']),
  lintels: elementMode(['lintels', 'lintel']),
  rip: elementMode(['rip', 'rips']),
  lintel: elementMode(['lintel', 'lintels']),
  position: elementMode(
    ['rip', 'lintel', 'rips', 'lintels', 'standard_rips'],
    [...ELEMENT_SUBSELECTS, 'member-position'],
  ),
  openings: elementMode(['opening'], ['connector', 'vertical-transmitter', 'load']),
  stiffening: elementMode(WALL_TYPES, ['opening', ...ELEMENT_SUBSELECTS]),

  // --- StructuralPlanning multi & load application ---
  'multi-edit': {
    page: 'structural-planning',
    selects: { kinds: ['planar'], multi: true },
    subSelects: [],
  },
  'line-load': {
    page: 'structural-planning',
    selects: { kinds: ['planar'], multi: false },
    subSelects: ['load'],
  },
  'point-load': {
    page: 'structural-planning',
    selects: { kinds: ['planar'], multi: false },
    subSelects: ['load'],
  },
  'area-load': {
    page: 'structural-planning',
    selects: { kinds: ['planar'], multi: false },
    subSelects: ['load'],
  },

  // --- StructuralPlanning drawing modes ---
  'draw-walls': drawMode(WALL_TYPES),
  'draw-openings': drawMode(WALL_TYPES, ['opening']),
  'edit-openings-3d': drawMode(WALL_TYPES, ['opening']),
  // These four render the full element form for a drawn-and-saved element, whose connector /
  // vertical-transmitter tabs set sub-selections just like their element modes do.
  'draw-beams': drawMode(['beams'], ELEMENT_SUBSELECTS),
  'draw-columns': drawMode(['columns'], ELEMENT_SUBSELECTS),
  'draw-purlins': drawMode(['purlins'], ELEMENT_SUBSELECTS),
  'draw-vertical-slabs': drawMode(['vertical_slabs']),
  'draw-vertical-roofs': drawMode(['vertical_roof_slabs'], ELEMENT_SUBSELECTS),
  'draw-slabs': drawMode(['slabs']),
  'draw-roofs': drawMode(['roof_slabs']),
  'wall-rip': drawMode(WALL_TYPES),
  'wall-lintel': drawMode(WALL_TYPES),

  // --- StructuralPlanning tool panes without a scene selection ---
  /** Stiffening segment picking (segments live in the structural planning store, not here). */
  selection: noSelectionMode(),
  building: noSelectionMode(),
  'storey-selection': noSelectionMode(),

  // --- Plan elements & comments ---
  comments: { ...planElementMode(), page: '*' },
  'plan-table': { ...planElementMode(), page: 'structural-planning' },
  'plan-image-card': { ...planElementMode(), page: 'structural-planning' },

  // --- IFC reference drawer (StructuralPlanning) ---
  'ifc-drawer': {
    page: 'structural-planning',
    selects: { kinds: ['ifc', 'planar'], multi: true },
    subSelects: [],
  },

  // --- IFC import page modes (former `ifcMode`) ---
  // Selection is deliberately untouched by tool switches on the IFC page, matching the legacy
  // behavior where changing `ifcMode` never cleared the IFC/planar selection.
  // NOTE: on this page the matrix is deliberately NOT the whole story — the guided steps
  // restrict which planar element types scene clicks may select via
  // `controlStore.planarSelectableTypes` (written by `IFCElementEditPanel`, read by
  // `useIFCPlanarSelection`), a per-step filter below the mode granularity modeled here.
  'ifc-building': ifcImportMode(),
  'ifc-settings': ifcImportMode(),
  'ifc-add': ifcImportMode(),
  'ifc-guided': ifcImportMode(),
  'ifc-alignment': ifcImportMode(),
  'ifc-filter': ifcImportMode(),
  'ifc-storey': ifcImportMode(),
  'ifc-type': ifcImportMode(),
  'ifc-draw-roofs': ifcImportMode(),
  'ifc-draw-slabs': ifcImportMode(),
  'ifc-draw-walls': ifcImportMode(),
  'ifc-draw-openings': ifcImportMode(),
  'ifc-auto-connect': ifcImportMode(),

  // --- Results modes ---
  'tensile-connectors-table': {
    page: 'results',
    selects: { kinds: ['planar'], multi: false },
    subSelects: ['connector'],
  },
  blockpad: {
    page: 'results',
    selects: { kinds: ['planar'], multi: true },
    subSelects: [],
  },
}

const DEFAULT_MODE: EditorMode = 'neutral'

/**
 * Modes whose scene clicks are owned by a dedicated tool (drawing controllers, load application,
 * stiffening segment picking, …) instead of the generic "click selects the element" listener.
 * `draw-openings` stays generic because its click handler branches internally; `draw-walls` and
 * `draw-columns` were never excluded by the legacy gate and keep that behavior.
 */
const GENERIC_SCENE_CLICK_EXCLUDED: ReadonlySet<EditorMode> = new Set<EditorMode>([
  'selection',
  'wall-rip',
  'line-load',
  'point-load',
  'area-load',
  'draw-vertical-roofs',
  'draw-vertical-slabs',
  'draw-slabs',
  'draw-roofs',
  'draw-beams',
  'draw-purlins',
  'edit-openings-3d',
])

/** Whether the generic scene click listener may select elements in the given mode. */
const isGenericSceneClickMode = (mode: EditorMode): boolean =>
  !GENERIC_SCENE_CLICK_EXCLUDED.has(mode)

export { MODE_SPECS, DEFAULT_MODE, isGenericSceneClickMode }
export type { ModeSpec, ModeSelectsSpec }
